import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, MapPin, CreditCard, Truck, Plus, Star, Pencil } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AddressFormModal from '../../components/common/AddressFormModal';
import PaymentMethods from '../../components/common/PaymentMethods';
import { cldImg } from '../../utils/cloudinary';
import { analyticsBeginCheckout, analyticsPurchase } from '../../utils/analytics';

const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh','Puducherry',
];

function StepIndicator({ step }) {
  const steps = [
    { n: 1, label: 'Address' },
    { n: 2, label: 'Review' },
    { n: 3, label: 'Payment' },
  ];
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((s, i) => (
        <div key={s.n} className="flex items-center">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${step >= s.n ? 'bg-brand-yellow text-white' : 'bg-gray-100 text-gray-400'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? 'bg-white text-brand-yellow' : 'bg-gray-300 text-white'}`}>{s.n}</span>
            <span className="hidden sm:block">{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-0.5 w-6 sm:w-12 ${step > s.n ? 'bg-brand-yellow' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

const EMPTY_ADDR = { name: '', phone: '', email: '', address_line1: '', address_line2: '', city: '', state: 'Tamil Nadu', pincode: '' };

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart, cartHasFreeShipping } = useCart();
  const { user } = useAuth();

  const { coupon, discount = 0, shippingFee: cartShippingFee, grandTotal: cartGrandTotal, selectedCourier, pincode: cartPincode } = location.state || {};

  const [step, setStep] = useState(1);
  const [addr, setAddr] = useState({
    ...EMPTY_ADDR,
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.mobile || user?.phone || '',
    pincode: cartPincode || '',
  });
  const [errors, setErrors] = useState({});
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [useManualForm, setUseManualForm] = useState(!user);
  const [addrModal, setAddrModal] = useState(null); // null | {} (add) | address (edit)
  const [payMethod, setPayMethod] = useState('upi');
  const [notes, setNotes] = useState('');
  const [placing, setPlacing] = useState(false);
  const [exitPopup, setExitPopup] = useState(false);
  const [fallbackShipping, setFallbackShipping] = useState(null);
  const [fallbackShippingEnabled, setFallbackShippingEnabled] = useState(true);
  const [freeShippingAbove, setFreeShippingAbove] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rates, setRates] = useState(null);
  const [ratesError, setRatesError] = useState(null);
  const [selectedCourierRate, setSelectedCourierRate] = useState(null);

  // Meta Pixel — InitiateCheckout (fire once when checkout opens)
  useEffect(() => {
    const items = cart.filter(i => !i.isFreeGift);
    if (items.length === 0) return;
    analyticsBeginCheckout({
      items: items.map(i => ({
        product_id: i.product_id, name: i.name, price: i.price, qty: i.qty,
        variant_label: i.variant_label,
      })),
      value: cartGrandTotal ?? cartTotal,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exit intent — intercept browser back
  useEffect(() => {
    const handlePop = (e) => {
      e.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setExitPopup(true);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const applyAddress = (a) => {
    setSelectedAddrId(a.id);
    setAddr(p => ({
      ...p,
      name: a.name || '',
      phone: a.phone || '',
      email: a.email || user?.email || p.email || '',
      address_line1: a.address_line1 || '',
      address_line2: a.address_line2 || '',
      city: a.city || '',
      state: a.state || 'Tamil Nadu',
      pincode: a.pincode || '',
    }));
  };

  // Load the logged-in user's saved addresses; auto-select the default one
  const fetchAddresses = async (selectId) => {
    try {
      const { data } = await api.get('/addresses');
      const list = data.addresses || [];
      setSavedAddresses(list);
      if (list.length === 0) { setUseManualForm(true); return list; }
      const pick = (selectId && list.find(a => a.id === selectId))
        || list.find(a => a.is_default) || list[0];
      if (pick) applyAddress(pick);
      setUseManualForm(false);
      return list;
    } catch { return []; }
  };

  useEffect(() => {
    if (user) fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    api.get('/settings/public').then(({ data }) => {
      const s = data.settings || {};
      const charge = s.shipping_charge !== '' && s.shipping_charge != null ? parseFloat(s.shipping_charge) : 0;
      setFallbackShipping(charge);
      const enabled = !(s.fallback_shipping_enabled === '0' || s.fallback_shipping_enabled === 'false' || s.fallback_shipping_enabled === false);
      setFallbackShippingEnabled(enabled);
      const freeAbove = s.free_shipping_above != null && s.free_shipping_above !== '' ? parseFloat(s.free_shipping_above) : null;
      setFreeShippingAbove(freeAbove);
      // Only use cartShippingFee from state if free shipping is NOT unlocked
      if (cartShippingFee != null) {
        const orderAmt = cartTotal - discount;
        const alreadyFree = cartHasFreeShipping || (freeAbove && freeAbove > 0 && orderAmt >= freeAbove);
        if (!alreadyFree) setSelectedCourierRate(cartShippingFee);
      }
    }).catch(() => {
      setFallbackShipping(0);
      setFallbackShippingEnabled(true);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    const pin = addr.pincode?.trim();
    if (!/^[0-9]{6}$/.test(pin)) {
      setRates(null);
      setRatesError(null);
      if (!selectedCourier) setSelectedCourierRate(null);
      return;
    }

    const fetchRates = async () => {
      setRatesLoading(true);
      setRates(null);
      setRatesError(null);
      try {
        const totalWeight = cart.reduce((sum, item) => sum + (item.weight || 500) * item.qty, 0) || 500;
        const { data } = await api.get('/shipping/rates', {
          params: {
            pincode: pin,
            weight: totalWeight,
          },
        });

        if (data.success && data.rates?.length > 0) {
          setRates(data.rates);
          setRatesError(null);
          if (!selectedCourier) {
            setSelectedCourierRate(data.rates[0].rate);
          }
        } else {
          setRates([]);
          setRatesError('unserviceable');
          if (!selectedCourier) setSelectedCourierRate(null);
        }
      } catch {
        setRates([]);
        setRatesError('api');
        if (!selectedCourier) setSelectedCourierRate(null);
      } finally {
        setRatesLoading(false);
      }
    };

    fetchRates();
  }, [addr.pincode, cart, selectedCourier]);

  const isFreeShipping = cartHasFreeShipping || (freeShippingAbove && freeShippingAbove > 0 && (cartTotal - discount) >= freeShippingAbove);
  const rawShippingFee = (selectedCourier && cartShippingFee != null)
    ? cartShippingFee
    : (selectedCourierRate != null ? selectedCourierRate : (fallbackShippingEnabled ? (fallbackShipping ?? null) : null));
  const shippingFee = isFreeShipping ? 0 : rawShippingFee;
  const finalTotal = shippingFee !== null ? cartTotal - discount + shippingFee : null;

  const validate = () => {
    const e = {};
    if (!addr.name.trim()) e.name = 'Name is required';
    if (!addr.phone.trim() || !/^\d{10}$/.test(addr.phone.trim())) e.phone = 'Valid 10-digit phone required';
    if (!addr.email.trim() || !/\S+@\S+\.\S+/.test(addr.email)) e.email = 'Valid email required';
    if (!addr.address_line1.trim()) e.address_line1 = 'Address is required';
    if (!addr.city.trim()) e.city = 'City is required';
    if (!addr.state) e.state = 'State is required';
    if (!addr.pincode.trim() || !/^\d{6}$/.test(addr.pincode.trim())) e.pincode = 'Valid 6-digit pincode required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!fallbackShippingEnabled && shippingFee === null) {
      toast.error('Shipping rate unavailable for this pincode. Please enter a valid pincode.');
      return;
    }

    setPlacing(true);
    try {
      const payload = {
        items: cart.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id || null,
          qty: item.qty,
          price: item.price,
        })),
        shipping_address: addr,
        notes,
        subtotal: cartTotal,
        discount,
        coupon_code: coupon?.code || null,
        coupon_id: coupon?.id || null,
        shipping_charge: shippingFee ?? 0,
        total: finalTotal ?? (cartTotal - discount),
        payment_method: payMethod,
        guest_name: user ? null : addr.name,
        guest_email: user ? null : addr.email,
        guest_phone: user ? null : addr.phone,
      };

      const { data: orderData } = await api.post('/orders', payload);

      // Meta Pixel Purchase fires here (not only on the thank-you page) because
      // guests can't fetch their order there — the API requires a login. The
      // orderKey de-dup in trackPurchase keeps the two call sites from double-counting.
      const firePurchase = () => analyticsPurchase({
        orderKey: orderData.order_number || orderData.order_id,
        items: cart.filter(i => !i.isFreeGift).map(i => ({
          product_id: i.product_id, name: i.name, price: i.price, qty: i.qty,
          variant_label: i.variant_label,
        })),
        value: finalTotal ?? (cartTotal - discount),
        shipping: shippingFee ?? 0,
      });

      // Online payment only — this store does not offer COD.
      // → open Razorpay checkout
      const { data: rzpData } = await api.post(`/orders/${orderData.order_id}/create-payment`);

      const options = {
        key: rzpData.key_id,
        amount: rzpData.amount,
        currency: rzpData.currency,
        name: 'TheYellove',
        description: `Order ${rzpData.order_number}`,
        order_id: rzpData.razorpay_order_id,
        prefill: {
          name: addr.name,
          email: addr.email,
          contact: addr.phone,
        },
        theme: { color: '#F5B400' },
        handler: async (response) => {
          try {
            await api.post(`/orders/${orderData.order_id}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            firePurchase();
            clearCart();
            navigate(`/thankyou?orderId=${orderData.order_id}&orderNo=${encodeURIComponent(orderData.order_number)}`, { replace: true });
          } catch {
            toast.error('Payment verification failed. Contact support.');
          }
        },
        modal: {
          ondismiss: () => {
            toast('Payment cancelled. Try again or choose a different payment method.', { icon: '⚠️' });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order');
    } finally {
      setPlacing(false);
    }
  };

  // Order success screen
if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <p className="text-gray-500 mb-4">Your cart is empty.</p>
        <Link to="/products" className="btn-primary px-6 py-3 rounded-xl">Shop Now</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">

      {/* Add / edit address modal */}
      {addrModal && (
        <AddressFormModal
          address={addrModal.id ? addrModal : null}
          onClose={() => setAddrModal(null)}
          onSaved={(saved) => { setAddrModal(null); setUseManualForm(false); fetchAddresses(saved?.id); }}
        />
      )}

      {/* Exit intent popup */}
      {exitPopup && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center px-4" onClick={() => setExitPopup(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center"
            style={{ animation: 'pop-in 0.25s cubic-bezier(0.175,0.885,0.32,1.275) forwards' }}
            onClick={e => e.stopPropagation()}>
            <div className="text-5xl mb-3">🛍️</div>
            <h2 className="text-lg font-black text-brand-dark mb-2">Wait! Your order isn't placed yet</h2>
            <p className="text-sm text-gray-500 mb-5">You're so close! Complete your order and get it delivered to your doorstep.</p>
            <button
              onClick={() => setExitPopup(false)}
              className="w-full bg-brand-yellow text-brand-dark font-bold py-3 rounded-xl text-sm mb-3 hover:opacity-90 transition-opacity">
              Complete My Order 🎉
            </button>
            <button
              onClick={() => { setExitPopup(false); navigate('/cart'); }}
              className="w-full text-gray-400 text-sm hover:text-gray-600 transition-colors">
              Go back to cart
            </button>
          </div>
          <style>{`@keyframes pop-in { 0%{transform:scale(0.85);opacity:0} 100%{transform:scale(1);opacity:1} }`}</style>
        </div>
      )}

      <h1 className="text-2xl font-bold text-brand-dark mb-6 text-center">Checkout</h1>
      <StepIndicator step={step} />

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left — steps */}
        <div className="flex-1 space-y-4">

          {/* Step 1: Address */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-5 py-4"
              onClick={() => step !== 1 && setStep(1)}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-green-100' : 'bg-brand-yellow-light'}`}>
                  <MapPin size={16} className={step >= 2 ? 'text-green-600' : 'text-brand-yellow'} />
                </div>
                <span className="font-semibold text-brand-dark">Delivery Address</span>
              </div>
              {step === 1 ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
            </button>

            {step === 1 && user && savedAddresses.length > 0 && !useManualForm && (
              <div className="px-5 pb-5 space-y-3">
                {savedAddresses.map(a => (
                  <div key={a.id}
                    onClick={() => applyAddress(a)}
                    className={`rounded-xl border-2 p-4 cursor-pointer relative transition-colors ${selectedAddrId === a.id ? 'border-brand-yellow bg-brand-yellow-light' : 'border-gray-100 hover:border-gray-200'}`}>
                    <div className="flex items-start gap-3">
                      <span className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedAddrId === a.id ? 'border-brand-yellow bg-brand-yellow' : 'border-gray-300'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-brand-dark text-sm">
                          {a.name}
                          {a.is_default && <span className="ml-2 text-xs font-bold text-brand-yellow inline-flex items-center gap-0.5"><Star size={10} className="fill-brand-yellow" /> Default</span>}
                        </p>
                        <p className="text-sm text-gray-500 mt-0.5">{a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ''}, {a.city}, {a.state} – {a.pincode}</p>
                        <p className="text-sm text-gray-400 mt-0.5">{a.phone}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setAddrModal(a); }} className="text-gray-300 hover:text-brand-yellow"><Pencil size={15} /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => setAddrModal({})}
                  className="w-full border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm font-semibold text-gray-500 hover:border-brand-yellow hover:text-brand-yellow flex items-center justify-center gap-1.5 transition-colors">
                  <Plus size={16} /> Add New Address
                </button>
                <button onClick={() => { if (validate()) setStep(2); }} className="btn-primary w-full py-3 rounded-xl font-semibold">
                  Deliver Here
                </button>
              </div>
            )}

            {step === 1 && (!user || savedAddresses.length === 0 || useManualForm) && (
              <div className="px-5 pb-5 space-y-4">
                {user && savedAddresses.length > 0 && (
                  <button onClick={() => setUseManualForm(false)} className="text-sm text-brand-yellow font-semibold hover:underline">
                    ← Choose a saved address
                  </button>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Full Name" error={errors.name}>
                    <input className={input(errors.name)} value={addr.name} onChange={e => setAddr(p => ({ ...p, name: e.target.value }))} placeholder="Your full name" />
                  </Field>
                  <Field label="Phone Number" error={errors.phone}>
                    <input className={input(errors.phone)} value={addr.phone} onChange={e => setAddr(p => ({ ...p, phone: e.target.value }))} placeholder="10-digit mobile number" maxLength={10} />
                  </Field>
                </div>
                <Field label="Email Address" error={errors.email}>
                  <input className={input(errors.email)} value={addr.email} onChange={e => setAddr(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" type="email" />
                </Field>
                <Field label="Address Line 1" error={errors.address_line1}>
                  <input className={input(errors.address_line1)} value={addr.address_line1} onChange={e => setAddr(p => ({ ...p, address_line1: e.target.value }))} placeholder="House / Flat / Street" />
                </Field>
                <Field label="Address Line 2 (optional)">
                  <input className={input()} value={addr.address_line2} onChange={e => setAddr(p => ({ ...p, address_line2: e.target.value }))} placeholder="Area / Landmark" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="City" error={errors.city}>
                    <input className={input(errors.city)} value={addr.city} onChange={e => setAddr(p => ({ ...p, city: e.target.value }))} placeholder="City" />
                  </Field>
                  <Field label="State" error={errors.state}>
                    <select className={input(errors.state)} value={addr.state} onChange={e => setAddr(p => ({ ...p, state: e.target.value }))}>
                      {STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Pincode" error={errors.pincode}>
                    <input className={input(errors.pincode)} value={addr.pincode} onChange={e => setAddr(p => ({ ...p, pincode: e.target.value }))} placeholder="6-digit pincode" maxLength={6} />
                  </Field>
                </div>
                <Field label="Order Notes (optional)">
                  <textarea className={input()} rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any special instructions..." />
                </Field>
                <button
                  onClick={() => { if (validate()) setStep(2); }}
                  className="btn-primary w-full py-3 rounded-xl font-semibold"
                >
                  Continue to Review
                </button>
              </div>
            )}

            {step > 1 && (
              <div className="px-5 pb-4 text-sm text-gray-600">
                <p className="font-semibold">{addr.name} · {addr.phone}</p>
                <p>{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}, {addr.city}, {addr.state} - {addr.pincode}</p>
              </div>
            )}
          </div>

          {/* Step 2: Review items */}
          {step >= 2 && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-4"
                onClick={() => step !== 2 && setStep(2)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-green-100' : 'bg-brand-yellow-light'}`}>
                    <Truck size={16} className={step >= 3 ? 'text-green-600' : 'text-brand-yellow'} />
                  </div>
                  <span className="font-semibold text-brand-dark">Review Items ({cart.length})</span>
                </div>
                {step === 2 ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </button>

              {step === 2 && (
                <div className="px-5 pb-5">
                  <div className="space-y-3 mb-5">
                    {cart.map(item => (
                      <div key={item.key} className="flex gap-3 items-center">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0">
                          {item.image
                            ? <img src={cldImg(item.image, 160)} alt={item.name} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-2xl">🛍️</div>
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-brand-dark line-clamp-1">{item.name}</p>
                          {item.variant_label && <p className="text-xs text-gray-400">{item.variant_label}</p>}
                          <p className="text-xs text-gray-500">Qty: {item.qty}</p>
                        </div>
                        <p className="text-sm font-bold text-brand-dark">₹{(item.price * item.qty).toLocaleString('en-IN')}</p>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setStep(3)} className="btn-primary w-full py-3 rounded-xl font-semibold">
                    Continue to Payment
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Payment */}
          {step >= 3 && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-50">
                <div className="w-8 h-8 rounded-full bg-brand-yellow-light flex items-center justify-center">
                  <CreditCard size={16} className="text-brand-yellow" />
                </div>
                <span className="font-semibold text-brand-dark">Payment Method</span>
              </div>
              <div className="px-5 py-4 space-y-3">
                {[
                  { value: 'upi', label: 'UPI / QR Code', sub: 'Pay via any UPI app', emoji: '📱' },
                  { value: 'card', label: 'Credit / Debit Card', sub: 'Visa, Mastercard, RuPay', emoji: '💳' },
                ].map(opt => (
                  <label key={opt.value} className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${payMethod === opt.value ? 'border-brand-yellow bg-brand-yellow-light' : 'border-gray-100 hover:border-gray-200'}`}>
                    <input type="radio" name="payMethod" value={opt.value} checked={payMethod === opt.value} onChange={() => setPayMethod(opt.value)} className="accent-brand-yellow" />
                    <span className="text-2xl">{opt.emoji}</span>
                    <div>
                      <p className="font-semibold text-brand-dark text-sm">{opt.label}</p>
                      <p className="text-xs text-gray-400">{opt.sub}</p>
                    </div>
                  </label>
                ))}

                <PaymentMethods className="mt-2" />

                <button
                  onClick={handlePlaceOrder}
                  disabled={placing}
                  className="btn-primary w-full py-4 rounded-xl font-bold text-base mt-2 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {placing ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Placing Order...
                    </>
                  ) : (
                    `Place Order · ₹${(finalTotal ?? cartTotal - discount).toLocaleString('en-IN')}`
                  )}
                </button>
                <p className="text-xs text-gray-400 text-center">By placing the order, you agree to our Terms & Conditions</p>
              </div>
            </div>
          )}
        </div>

        {/* Right — order summary */}
        <div className="lg:w-80">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24">
            <h3 className="font-bold text-brand-dark mb-4">Order Summary</h3>
            <div className="space-y-2 mb-4">
              {cart.map(item => (
                <div key={item.key} className="flex gap-3 items-center">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0">
                    {item.image
                      ? <img src={cldImg(item.image, 160)} alt={item.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-xl">🛍️</div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-brand-dark line-clamp-2">{item.name}</p>
                    {item.variant_label && <p className="text-xs text-gray-400">{item.variant_label}</p>}
                    <p className="text-xs text-gray-500">Qty: {item.qty}</p>
                  </div>
                  <p className="text-xs font-bold text-brand-dark whitespace-nowrap">₹{(item.price * item.qty).toLocaleString('en-IN')}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-3 space-y-2.5 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal ({cart.length} items)</span>
                <span>₹{cartTotal.toLocaleString('en-IN')}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Coupon ({coupon?.code})</span>
                  <span>- ₹{discount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <div>
                  <span>Shipping</span>
                  {isFreeShipping ? (
                    <p className="text-xs text-green-600 font-semibold">🎉 Free shipping unlocked!</p>
                  ) : ratesLoading ? (
                    <p className="text-xs text-gray-400">Checking rates for pincode {addr.pincode}</p>
                  ) : ratesError === 'unserviceable' ? (
                    <p className="text-xs text-red-500">Delivery not available for pincode {addr.pincode}</p>
                  ) : ratesError === 'api' ? (
                    <p className="text-xs text-gray-400">Unable to fetch shipping rates{fallbackShippingEnabled ? ', using default charge' : ''}</p>
                  ) : addr.pincode ? (
                    <p className="text-xs text-gray-400">Shipping based on pincode {addr.pincode}</p>
                  ) : (
                    <p className="text-xs text-gray-400">Enter pincode in address to calculate shipping</p>
                  )}
                </div>
                <span className={shippingFee === 0 ? 'text-green-600 font-semibold' : ''}>
                  {shippingFee === null ? <span className="text-gray-400 text-xs">Calculating...</span>
                    : shippingFee === 0 ? 'FREE'
                    : `₹${shippingFee}`}
                </span>
              </div>
              <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-brand-dark">
                <span>Total</span>
                <span>{finalTotal === null ? '...' : `₹${finalTotal.toLocaleString('en-IN')}`}</span>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              {['🔒 100% Secure Checkout', '🚚 Fast Delivery'].map(t => (
                <div key={t} className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-brand-red mt-1">{error}</p>}
    </div>
  );
}

function input(error) {
  return `w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-brand-yellow transition-colors ${error ? 'border-brand-red bg-red-50' : 'border-gray-200'}`;
}
